import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Users, Plus, Edit2, Trash2, Mail, Shield, Clock, Check, X, MessageSquare } from 'lucide-react';
import { theme } from '../styles/theme';
import { useAuth } from '../context/AuthContext';
import ContactAdmin from './ContactAdmin';

// Types
interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  job_title?: string;
  organization_name?: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

interface CreateUserData {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  job_title?: string;
  organization_name?: string;
  is_admin: boolean;
}

// Styled Components
const AdminContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${theme.spacing[6]};
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing[8]};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.textPrimary};
    display: flex;
    align-items: center;
    gap: ${theme.spacing[3]};
    margin-bottom: ${theme.spacing[6]};
  }
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid ${theme.colors.border};
  margin-bottom: ${theme.spacing[8]};
`;

const TabsList = styled.div`
  display: flex;
  gap: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  background: ${({ $active }) => $active ? theme.colors.surface : 'transparent'};
  border: 1px solid ${({ $active }) => $active ? theme.colors.border : 'transparent'};
  border-bottom: ${({ $active }) => $active ? `1px solid ${theme.colors.surface}` : '1px solid transparent'};
  border-radius: ${theme.borderRadius.lg} ${theme.borderRadius.lg} 0 0;
  color: ${({ $active }) => $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  font-weight: ${({ $active }) => $active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  position: relative;
  margin-bottom: -1px;
  
  &:hover {
    background: ${({ $active }) => $active ? theme.colors.surface : theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }
`;

const TabContent = styled.div`
  min-height: 400px;
`;

const UserManagementHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: ${theme.spacing[8]};
  
  h2 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.textPrimary};
    display: flex;
    align-items: center;
    gap: ${theme.spacing[3]};
  }
`;

const CreateButton = styled.button`
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: background-color ${theme.transitions.base};
  
  &:hover {
    background: ${theme.colors.primary[600]};
  }
`;

const UserTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
`;

const TableHeader = styled.th`
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[4]};
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  border-bottom: 1px solid ${theme.colors.border};
`;

const TableCell = styled.td`
  padding: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  color: ${theme.colors.textPrimary};
  
  &:last-child {
    width: 120px;
  }
`;

const StatusBadge = styled.span<{ active?: boolean; admin?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  
  ${props => props.active ? `
    background: ${theme.colors.success[100]};
    color: ${theme.colors.success[700]};
  ` : `
    background: ${theme.colors.error[100]};
    color: ${theme.colors.error[700]};
  `}
  
  ${props => props.admin && `
    background: ${theme.colors.primary[100]};
    color: ${theme.colors.primary[700]};
  `}
`;

const ActionButton = styled.button<{ variant?: 'danger' }>`
  background: transparent;
  border: 1px solid ${props => props.variant === 'danger' ? theme.colors.error[500] : theme.colors.border};
  color: ${props => props.variant === 'danger' ? theme.colors.error[600] : theme.colors.textMuted};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing[2]};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    background: ${props => props.variant === 'danger' ? theme.colors.error[100] : theme.colors.backgroundSecondary};
    color: ${props => props.variant === 'danger' ? theme.colors.error[700] : theme.colors.textPrimary};
  }
`;

const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  width: 100%;
  max-width: 500px;
  margin: ${theme.spacing[4]};
  box-shadow: ${theme.shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
  
  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  padding: ${theme.spacing[1]};
  
  &:hover {
    color: ${theme.colors.textPrimary};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
`;

const Checkbox = styled.input`
  margin-right: ${theme.spacing[2]};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textPrimary};
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
  margin-top: ${theme.spacing[4]};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  ${props => props.$variant === 'primary' ? `
    background: ${theme.colors.primary[500]};
    color: white;
    border: none;
    
    &:hover {
      background: ${theme.colors.primary[600]};
    }
  ` : `
    background: transparent;
    color: ${theme.colors.textSecondary};
    border: 1px solid ${theme.colors.border};
    
    &:hover {
      background: ${theme.colors.backgroundSecondary};
    }
  `}
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[100]};
  color: ${theme.colors.error[700]};
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  margin-bottom: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.textMuted};
`;

// Main Component
const AdminDashboard: React.FC = () => {
  const { state } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'contact'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    job_title: '',
    organization_name: '',
    is_admin: false,
  });

  // API calls
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 
      (process.env.NODE_ENV === 'production' 
        ? 'https://api-beta-open-tender.nat-quest.com' 
        : 'http://localhost:2024');
    const response = await fetch(`${apiBaseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${state.accessToken}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/admin/users');
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (userData: CreateUserData) => {
    try {
      await apiCall('/admin/users', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      setShowCreateModal(false);
      setFormData({ email: '', password: '', first_name: '', last_name: '', job_title: '', organization_name: '', is_admin: false });
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const updateUser = async (userId: string, userData: CreateUserData) => {
    try {
      await apiCall(`/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
      setShowEditModal(false);
      setSelectedUser(null);
      setFormData({ email: '', password: '', first_name: '', last_name: '', job_title: '', organization_name: '', is_admin: false });
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const deleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await apiCall(`/admin/users/${userId}`, {
        method: 'DELETE',
      });
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  useEffect(() => {
    if (state.isAuthenticated && state.user?.is_admin) {
      loadUsers();
    }
  }, [state.isAuthenticated, state.user]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUser) {
      updateUser(selectedUser.id, formData);
    } else {
      createUser(formData);
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '', // Don't pre-fill password
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      job_title: user.job_title || '',
      organization_name: user.organization_name || '',
      is_admin: user.is_admin,
    });
    setShowEditModal(true);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedUser(null);
    setFormData({ email: '', password: '', first_name: '', last_name: '', job_title: '', organization_name: '', is_admin: false });
    setError(null);
  };

  if (!state.user?.is_admin) {
    return (
      <AdminContainer>
        <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <Header>
        <h1>
          <Shield size={28} />
          Administration
        </h1>
      </Header>

      <TabsContainer>
        <TabsList>
          <Tab 
            $active={activeTab === 'users'} 
            onClick={() => setActiveTab('users')}
          >
            <Users size={16} />
            User Management
          </Tab>
          <Tab 
            $active={activeTab === 'contact'} 
            onClick={() => setActiveTab('contact')}
          >
            <MessageSquare size={16} />
            Contact Submissions
          </Tab>
        </TabsList>
      </TabsContainer>

      <TabContent>
        {activeTab === 'users' ? (
          <>
            <UserManagementHeader>
              <h2>
                <Users size={24} />
                User Management
              </h2>
              <CreateButton onClick={() => setShowCreateModal(true)}>
                <Plus size={16} />
                Create User
              </CreateButton>
            </UserManagementHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading ? (
        <LoadingSpinner>Loading users...</LoadingSpinner>
      ) : (
        <UserTable>
          <thead>
            <tr>
              <TableHeader>Name</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Organization</TableHeader>
              <TableHeader>Job Title</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Role</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <TableCell>
                  <div>
                    {user.first_name || user.last_name ? (
                      <div style={{ fontWeight: theme.typography.fontWeight.medium }}>
                        {user.first_name} {user.last_name}
                      </div>
                    ) : (
                      <span style={{ color: theme.colors.textMuted, fontStyle: 'italic' }}>No name</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                    <Mail size={16} />
                    {user.email}
                  </div>
                </TableCell>
                <TableCell>
                  {user.organization_name || <span style={{ color: theme.colors.textMuted, fontStyle: 'italic' }}>-</span>}
                </TableCell>
                <TableCell>
                  {user.job_title || <span style={{ color: theme.colors.textMuted, fontStyle: 'italic' }}>-</span>}
                </TableCell>
                <TableCell>
                  <StatusBadge active={user.is_active}>
                    {user.is_active ? <Check size={12} /> : <X size={12} />}
                    {user.is_active ? 'Active' : 'Inactive'}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  {user.is_admin && (
                    <StatusBadge admin>
                      <Shield size={12} />
                      Admin
                    </StatusBadge>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: theme.spacing[2] }}>
                    <ActionButton onClick={() => openEditModal(user)}>
                      <Edit2 size={16} />
                    </ActionButton>
                    <ActionButton 
                      variant="danger" 
                      onClick={() => deleteUser(user.id)}
                      disabled={user.id === state.user?.id}
                    >
                      <Trash2 size={16} />
                    </ActionButton>
                  </div>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </UserTable>
      )}

      {/* Create/Edit User Modal */}
      <Modal $show={showCreateModal || showEditModal}>
        <ModalContent>
          <ModalHeader>
            <h2>{selectedUser ? 'Edit User' : 'Create User'}</h2>
            <CloseButton onClick={closeModals}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </FormGroup>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing[4] }}>
              <FormGroup>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name || ''}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name || ''}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
              </FormGroup>
            </div>

            <FormGroup>
              <Label htmlFor="organization_name">Organization Name</Label>
              <Input
                id="organization_name"
                type="text"
                value={formData.organization_name || ''}
                onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="job_title">Job Title</Label>
              <Input
                id="job_title"
                type="text"
                value={formData.job_title || ''}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">
                {selectedUser ? 'New Password (leave blank to keep current)' : 'Password'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!selectedUser}
              />
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                />
                Admin privileges
              </CheckboxLabel>
            </FormGroup>

            <ButtonGroup>
              <Button type="button" onClick={closeModals}>
                Cancel
              </Button>
              <Button type="submit" $variant="primary">
                {selectedUser ? 'Update User' : 'Create User'}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>
          </>
        ) : (
          <ContactAdmin />
        )}
      </TabContent>
    </AdminContainer>
  );
};

export default AdminDashboard;